import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronRight, ChevronDown, FolderOpen, FileText, AlertCircle, Pencil, Trash2, Unlink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AddParentLevelModal } from './modals/add-parent-level-modal';
import { AddChildLevelModal } from './modals/add-child-level-modal';
import { AddExistingCostCentreModal } from './modals/add-existing-cost-centre-modal';
import { CreateCostCentrePanel } from './create-cost-centre-panel';
import { UnsavedChangesDialog } from './modals/unsaved-changes-dialog';
import { EditLevelPanel } from './edit-level-panel';
import { DeleteLevelDialog } from './modals/delete-level-dialog';
import { RemoveFromStructureDialog } from './modals/remove-from-structure-dialog';
import { toast } from 'sonner';
import type { HierarchicalCostCentre } from './cost-centres-hierarchical-table';
import type { CostCentre } from './cost-centres-table';

interface ConfigureStructureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  costCentres: HierarchicalCostCentre[];
  onSave: (updatedStructure: HierarchicalCostCentre[]) => Promise<void>;
}

interface TreeNode extends HierarchicalCostCentre {
  children?: TreeNode[];
  notes?: string;
}

export function ConfigureStructureModal({
  open,
  onOpenChange,
  projectName,
  costCentres,
  onSave,
}: ConfigureStructureModalProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [workingStructure, setWorkingStructure] = useState<TreeNode[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sub-modal states
  const [addParentOpen, setAddParentOpen] = useState(false);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [editLevelOpen, setEditLevelOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<TreeNode | null>(null);
  const [deleteLevelOpen, setDeleteLevelOpen] = useState(false);
  const [deletingLevel, setDeletingLevel] = useState<TreeNode | null>(null);
  const [removeFromStructureOpen, setRemoveFromStructureOpen] = useState(false);
  const [removingCostCentre, setRemovingCostCentre] = useState<TreeNode | null>(null);
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  // Initialize working structure when modal opens
  useMemo(() => {
    if (open) {
      setWorkingStructure(buildTreeStructure(costCentres));
      setHasChanges(false);
      setSaveError(null);
    }
  }, [open, costCentres]);

  // Build tree structure from flat data
  function buildTreeStructure(items: HierarchicalCostCentre[]): TreeNode[] {
    const map = new Map<number, TreeNode>();
    const roots: TreeNode[] = [];

    // First pass: create map of all items
    items.forEach(item => {
      map.set(item.id, { ...item, children: [] });
    });

    // Second pass: build tree structure
    items.forEach(item => {
      const node = map.get(item.id)!;
      if (item.parentId && map.has(item.parentId)) {
        const parent = map.get(item.parentId)!;
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  }

  // Get selected node
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    
    function findNode(nodes: TreeNode[]): TreeNode | null {
      for (const node of nodes) {
        if (node.id === selectedNodeId) return node;
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    }
    
    return findNode(workingStructure);
  }, [selectedNodeId, workingStructure]);

  // Check if selected node is a parent/child node (not a cost centre)
  // Parent/child nodes don't have a type field, while cost centre nodes do
  const isParentChildNode = useMemo(() => {
    if (!selectedNode) return false;
    return !selectedNode.type || selectedNode.type.trim() === '';
  }, [selectedNode]);

  // Get available cost centres (not in structure)
  const availableCostCentres = useMemo(() => {
    const inStructure = new Set<number>();
    
    function collectIds(nodes: TreeNode[]) {
      nodes.forEach(node => {
        inStructure.add(node.id);
        if (node.children) collectIds(node.children);
      });
    }
    
    collectIds(workingStructure);
    return costCentres.filter(cc => !inStructure.has(cc.id));
  }, [workingStructure, costCentres]);

  // Toggle node expansion
  const toggleNode = (nodeId: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Expand all nodes
  const expandAll = () => {
    const allIds = new Set<number>();
    
    function collectIds(nodes: TreeNode[]) {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    }
    
    collectIds(workingStructure);
    setExpandedNodes(allIds);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (hasChanges) {
      setPendingClose(true);
      setUnsavedChangesOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Flatten tree structure back to array
      const flattenedStructure: HierarchicalCostCentre[] = [];
      
      function flatten(nodes: TreeNode[], parentId: number | null = null) {
        nodes.forEach(node => {
          flattenedStructure.push({
            ...node,
            parentId,
            children: undefined, // Remove children from flattened structure
          });
          if (node.children) {
            flatten(node.children, node.id);
          }
        });
      }
      
      flatten(workingStructure);
      
      await onSave(flattenedStructure);
      setHasChanges(false);
      onOpenChange(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle add parent level
  const handleAddParent = (code: string, name: string, notes: string) => {
    const newParent: TreeNode = {
      id: -Date.now(),
      code,
      name,
      type: '',
      billable: false,
      costsBudget: 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: 0,
      actualRevenue: 0,
      hoursBudget: 0,
      actualHours: 0,
      children: selectedNode ? [selectedNode] : [],
      notes,
    };
    
    if (selectedNode) {
      // Wrap selected node in the new parent
      const updateStructure = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.map(node => {
          if (node.id === selectedNodeId) {
            return newParent;
          }
          if (node.children) {
            return { ...node, children: updateStructure(node.children) };
          }
          return node;
        });
      };
      setWorkingStructure(updateStructure(workingStructure));
    } else {
      // No selection: add new parent as a top-level structural node
      setWorkingStructure([...workingStructure, newParent]);
    }
    
    setSelectedNodeId(newParent.id);
    setExpandedNodes(prev => new Set([...prev, newParent.id]));
    setHasChanges(true);
    setAddParentOpen(false);
  };

  // Handle add child level
  const handleAddChild = (code: string, name: string, notes: string) => {
    const newChild: TreeNode = {
      id: -Date.now(),
      code,
      name,
      type: '',
      billable: false,
      costsBudget: 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: 0,
      actualRevenue: 0,
      hoursBudget: 0,
      actualHours: 0,
      children: [],
      notes,
    };
    
    if (selectedNode) {
      // Add child under the selected node
      const updateStructure = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.map(node => {
          if (node.id === selectedNodeId) {
            return {
              ...node,
              children: [...(node.children || []), newChild],
            };
          }
          if (node.children) {
            return { ...node, children: updateStructure(node.children) };
          }
          return node;
        });
      };
      setWorkingStructure(updateStructure(workingStructure));
      setExpandedNodes(prev => new Set([...prev, selectedNodeId!]));
    } else {
      // No selection: add as a top-level structural node
      setWorkingStructure([...workingStructure, newChild]);
    }
    
    setSelectedNodeId(newChild.id);
    setHasChanges(true);
    setAddChildOpen(false);
  };

  // Handle add existing cost centres
  const handleAddExisting = (costCentreIds: number[]) => {
    if (!selectedNode) return;
    
    const newChildren = costCentreIds.map(id => {
      const cc = availableCostCentres.find(c => c.id === id)!;
      return {
        ...cc,
        children: [],
      };
    });
    
    // Update structure
    const updateStructure = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === selectedNodeId) {
          return {
            ...node,
            children: [...(node.children || []), ...newChildren],
          };
        }
        if (node.children) {
          return { ...node, children: updateStructure(node.children) };
        }
        return node;
      });
    };
    
    setWorkingStructure(updateStructure(workingStructure));
    setExpandedNodes(prev => new Set([...prev, selectedNodeId!]));
    setHasChanges(true);
    setAddExistingOpen(false);
  };

  // Handle create new cost centre
  const handleCreateNew = (data: Omit<CostCentre, 'id'>) => {
    if (!selectedNode) return;
    
    const newCostCentre: TreeNode = {
      id: -Date.now(),
      code: data.code,
      name: data.name,
      type: data.type,
      billable: data.billable,
      costsBudget: data.costsBudget || 0,
      actualCosts: data.actualCosts || 0,
      committedCosts: data.committedCosts || 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: data.actualRevenue || 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: data.actualHours || 0,
      children: [],
    };
    
    // Update structure
    const updateStructure = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === selectedNodeId) {
          return {
            ...node,
            children: [...(node.children || []), newCostCentre],
          };
        }
        if (node.children) {
          return { ...node, children: updateStructure(node.children) };
        }
        return node;
      });
    };
    
    setWorkingStructure(updateStructure(workingStructure));
    setSelectedNodeId(newCostCentre.id);
    setExpandedNodes(prev => new Set([...prev, selectedNodeId!]));
    setHasChanges(true);
    setCreateNewOpen(false);
  };

  // Handle edit level
  const handleEditLevel = (nodeId: number) => {
    const node = findNodeById(nodeId, workingStructure);
    if (node) {
      setEditingLevel(node);
      setEditLevelOpen(true);
    }
  };

  // Find node by ID
  const findNodeById = (nodeId: number, nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children) {
        const found = findNodeById(nodeId, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle save edit level
  const handleSaveEditLevel = (id: number, name: string, notes: string) => {
    // Update structure
    const updateStructure = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === id) {
          return { ...node, name, notes };
        }
        if (node.children) {
          return { ...node, children: updateStructure(node.children) };
        }
        return node;
      });
    };

    setWorkingStructure(updateStructure(workingStructure));
    setHasChanges(true);
    setEditLevelOpen(false);
  };

  // Handle delete level
  const handleDeleteLevel = (nodeId: number) => {
    const node = findNodeById(nodeId, workingStructure);
    if (node) {
      setDeletingLevel(node);
      setDeleteLevelOpen(true);
    }
  };

  // Count cost centre children (not parent/child levels)
  const countCostCentreChildren = (node: TreeNode): number => {
    if (!node.children || node.children.length === 0) return 0;
    
    let count = 0;
    for (const child of node.children) {
      // Check if child is a cost centre (has type field)
      if (child.type && child.type.trim() !== '') {
        count++;
      }
    }
    return count;
  };

  // Handle confirm delete level
  const handleConfirmDeleteLevel = () => {
    if (!deletingLevel) return;

    const childCostCentres: TreeNode[] = [];
    
    // Collect all cost centre children (recursively)
    const collectCostCentres = (node: TreeNode) => {
      if (!node.children) return;
      
      for (const child of node.children) {
        // If it's a cost centre (has type), add it to the list
        if (child.type && child.type.trim() !== '') {
          childCostCentres.push(child);
        }
        // If it's a parent/child level, recurse into it
        else {
          collectCostCentres(child);
        }
      }
    };

    collectCostCentres(deletingLevel);

    // Remove the level from the structure
    const removeNode = (nodes: TreeNode[], nodeId: number): TreeNode[] => {
      return nodes.filter(node => {
        if (node.id === nodeId) {
          return false; // Remove this node
        }
        if (node.children) {
          node.children = removeNode(node.children, nodeId);
        }
        return true;
      });
    };

    setWorkingStructure(removeNode(workingStructure, deletingLevel.id));
    setHasChanges(true);
    setDeleteLevelOpen(false);
    
    // Clear selection if the deleted node was selected
    if (selectedNodeId === deletingLevel.id) {
      setSelectedNodeId(null);
    }

    // Show success toast
    if (childCostCentres.length > 0) {
      toast.success(
        `Level deleted. ${childCostCentres.length} cost centre${childCostCentres.length !== 1 ? 's' : ''} moved to Add Existing.`
      );
    } else {
      toast.success('Level deleted successfully');
    }
  };

  // Handle remove from structure
  const handleRemoveFromStructure = (nodeId: number) => {
    const node = findNodeById(nodeId, workingStructure);
    if (node) {
      setRemovingCostCentre(node);
      setRemoveFromStructureOpen(true);
    }
  };

  // Handle confirm remove from structure
  const handleConfirmRemoveFromStructure = () => {
    if (!removingCostCentre) return;

    // Remove the cost centre from the structure
    const removeNode = (nodes: TreeNode[], nodeId: number): TreeNode[] => {
      return nodes.filter(node => {
        if (node.id === nodeId) {
          return false; // Remove this node
        }
        if (node.children) {
          node.children = removeNode(node.children, nodeId);
        }
        return true;
      });
    };

    setWorkingStructure(removeNode(workingStructure, removingCostCentre.id));
    setHasChanges(true);
    setRemoveFromStructureOpen(false);
    
    // Clear selection if the removed node was selected
    if (selectedNodeId === removingCostCentre.id) {
      setSelectedNodeId(null);
    }

    // Show success toast
    toast.success('Cost centre removed from structure');
  };

  // Handle unsaved changes dialog actions
  const handleDiscardChanges = () => {
    setHasChanges(false);
    setUnsavedChangesOpen(false);
    if (pendingClose) {
      setPendingClose(false);
      onOpenChange(false);
    }
  };

  const handleSaveFromDialog = async () => {
    setUnsavedChangesOpen(false);
    await handleSave();
    if (pendingClose) {
      setPendingClose(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Configure Structure</DialogTitle>
            <DialogDescription>{projectName}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Toolbar */}
            <div className="space-y-3">
              {/* Status Indicator */}
              <div className="text-sm text-muted-foreground">
                {selectedNode ? (
                  <span>
                    Selected: <span className="font-medium text-foreground">{selectedNode.name}</span>
                    {!isParentChildNode && (
                      <span className="ml-2 text-xs text-warning-600">
                        (Cost centre - select a parent/child node to add cost centres)
                      </span>
                    )}
                  </span>
                ) : (
                  'Select a node to add parent/child or cost centres, or use Add Parent/Add Child to create a new level'
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setAddParentOpen(true)}
                  title={selectedNode ? 'Add a parent level above the selected node' : 'Add a new top-level parent node'}
                >
                  Add Parent
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setAddChildOpen(true)}
                  title={selectedNode ? 'Add a child level below the selected node' : 'Add a new top-level child node'}
                >
                  Add Child
                </Button>

                <Separator orientation="vertical" className="h-6" />

                <Button
                  variant="outline"
                  size="sm"
                  disabled={!selectedNode || !isParentChildNode}
                  onClick={() => setAddExistingOpen(true)}
                  title={
                    !selectedNode 
                      ? 'Please select a node first' 
                      : !isParentChildNode 
                      ? 'Please select a parent/child node (not a cost centre)' 
                      : "Add cost centres that exist but aren't in the structure"
                  }
                >
                  Add Existing
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!selectedNode || !isParentChildNode}
                  onClick={() => setCreateNewOpen(true)}
                  title={
                    !selectedNode 
                      ? 'Please select a node first' 
                      : !isParentChildNode 
                      ? 'Please select a parent/child node (not a cost centre)' 
                      : 'Create a brand new cost centre'
                  }
                >
                  Add New
                </Button>
              </div>
            </div>

            <Separator />

            {/* Tree Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  Expand All
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  Collapse All
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Click on a node to select it, then use the buttons above to add parent/child or cost centres
              </p>

            </div>

            {/* Tree View */}
            <div className="flex-1 overflow-auto border rounded-md p-4">
              {workingStructure.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No cost centres in structure. Add existing cost centres or create new ones.
                </div>
              ) : (
                <TreeView
                  nodes={workingStructure}
                  selectedNodeId={selectedNodeId}
                  expandedNodes={expandedNodes}
                  onSelectNode={setSelectedNodeId}
                  onToggleNode={toggleNode}
                  onEditNode={handleEditLevel}
                  onDeleteNode={handleDeleteLevel}
                  onRemoveFromStructure={handleRemoveFromStructure}
                />
              )}
            </div>

            {/* Error Display */}
            {saveError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modals */}
      <AddParentLevelModal
        open={addParentOpen}
        onOpenChange={setAddParentOpen}
        selectedNodeName={selectedNode?.name || ''}
        existingCodes={costCentres.map(cc => cc.code)}
        onAdd={handleAddParent}
      />

      <AddChildLevelModal
        open={addChildOpen}
        onOpenChange={setAddChildOpen}
        selectedNodeName={selectedNode?.name || ''}
        existingCodes={costCentres.map(cc => cc.code)}
        onAdd={handleAddChild}
      />

      <AddExistingCostCentreModal
        open={addExistingOpen}
        onOpenChange={setAddExistingOpen}
        selectedNodeName={selectedNode?.name || ''}
        availableCostCentres={availableCostCentres}
        onAdd={handleAddExisting}
      />

      <CreateCostCentrePanel
        open={createNewOpen}
        onOpenChange={setCreateNewOpen}
        parentNodeName={selectedNode?.name || ''}
        existingCodes={costCentres.map(cc => cc.code)}
        onSave={handleCreateNew}
      />

      <UnsavedChangesDialog
        open={unsavedChangesOpen}
        onOpenChange={setUnsavedChangesOpen}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveFromDialog}
      />

      <EditLevelPanel
        open={editLevelOpen}
        onOpenChange={setEditLevelOpen}
        level={editingLevel ? {
          id: editingLevel.id,
          code: editingLevel.code,
          name: editingLevel.name,
          notes: editingLevel.notes,
        } : null}
        isParentLevel={editingLevel ? (!editingLevel.type || editingLevel.type.trim() === '') : false}
        onSave={handleSaveEditLevel}
      />

      <DeleteLevelDialog
        open={deleteLevelOpen}
        onOpenChange={setDeleteLevelOpen}
        levelName={deletingLevel?.name || ''}
        childCount={deletingLevel ? countCostCentreChildren(deletingLevel) : 0}
        onConfirm={handleConfirmDeleteLevel}
      />

      <RemoveFromStructureDialog
        open={removeFromStructureOpen}
        onOpenChange={setRemoveFromStructureOpen}
        costCentreName={removingCostCentre?.name || ''}
        onConfirm={handleConfirmRemoveFromStructure}
      />
    </>
  );
}

// Tree View Component
interface TreeViewProps {
  nodes: TreeNode[];
  selectedNodeId: number | null;
  expandedNodes: Set<number>;
  onSelectNode: (nodeId: number) => void;
  onToggleNode: (nodeId: number) => void;
  onEditNode: (nodeId: number) => void;
  onDeleteNode: (nodeId: number) => void;
  onRemoveFromStructure: (nodeId: number) => void;
  level?: number;
}

function TreeView({
  nodes,
  selectedNodeId,
  expandedNodes,
  onSelectNode,
  onToggleNode,
  onEditNode,
  onDeleteNode,
  onRemoveFromStructure,
  level = 0,
}: TreeViewProps) {
  return (
    <div className="space-y-1">
      {nodes.map(node => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.id);
        const isSelected = selectedNodeId === node.id;
        const isParentChildNode = !node.type || node.type.trim() === '';

        return (
          <div key={node.id}>
            <div
              className={cn(
                'group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                'hover:bg-accent',
                isSelected && 'bg-primary/10 border border-primary'
              )}
              style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
              onClick={() => onSelectNode(node.id)}
            >
              {hasChildren ? (
                <button
                  className="p-0.5 hover:bg-accent rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleNode(node.id);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              ) : (
                <span className="w-5" />
              )}
              
              {hasChildren ? (
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground" />
              )}
              
              <span className="font-medium">{node.name}</span>
              <span className="text-sm text-muted-foreground">({node.code})</span>
              
              {/* Action buttons */}
              <div className={cn(
                'ml-auto flex items-center gap-1 transition-opacity',
                'opacity-0 group-hover:opacity-100'
              )}>
                {isParentChildNode ? (
                  // Parent/child node actions
                  <>
                    <button
                      className="p-1 rounded hover:bg-accent-foreground/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditNode(node.id);
                      }}
                      title="Edit level"
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-warning-100 dark:hover:bg-warning-950"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteNode(node.id);
                      }}
                      title="Permanently delete this structural level. Any cost centres underneath will be moved to Add Existing."
                    >
                      <Trash2 className="h-3.5 w-3.5 text-warning-600" />
                    </button>
                  </>
                ) : (
                  // Cost centre node actions
                  <button
                    className="p-1 rounded hover:bg-warning-100 dark:hover:bg-warning-950"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromStructure(node.id);
                    }}
                    title="Remove from hierarchy. Cost centre remains in project and can be re-added."
                  >
                    <Unlink className="h-3.5 w-3.5 text-warning-600" />
                  </button>
                )}
              </div>
            </div>

            {hasChildren && isExpanded && (
              <TreeView
                nodes={node.children!}
                selectedNodeId={selectedNodeId}
                expandedNodes={expandedNodes}
                onSelectNode={onSelectNode}
                onToggleNode={onToggleNode}
                onEditNode={onEditNode}
                onDeleteNode={onDeleteNode}
                onRemoveFromStructure={onRemoveFromStructure}
                level={level + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
